"use client";

import { type CSSProperties, useState, useTransition } from "react";
import { useParams } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import classNames from "classnames";
import { Button, Spinner } from "@cappelendamm/design-system/react";
import { PAYMENT_PROVIDERS } from "@cappelendamm/e-commerce-services";
import VippsLogo from "@/assets/payment-2/VippsLogo.svg";
import { useTrackCheckoutSubmit } from "@/checkout/tracking";
import { startVippsExpressCheckout } from "../_actions";
import { useVippsHurtigkasseFlag } from "../useVippsHurtigkasseFlag";

// Vipps orange for both the filled button (white text and logo) and the
// outlined one, where the border, text and logo take this colour instead.
const VIPPS_BUTTON_STYLE = {
  "--cvr-button-background": "#ff5b24",
  "--cvr-button-contrast": "#ffffff",
} as CSSProperties;

type VippsExpressButtonProps = {
  /** Pay for one copy of this product instead of the cart. */
  sku?: string;
  label?: string;
  /** Filled in the cart, outlined next to "Legg i handlekurv" on product pages. */
  variant?: "primary" | "secondary";
  className?: string;
};

/**
 * Vipps Hurtigkasse: sends the visitor to Vipps, where they share their
 * address and approve the payment, and comes back to the receipt.
 *
 * Shown when the PostHog flag is on, and only to signed-out visitors: signed-in
 * customers keep the ordinary checkout, which knows their customer record,
 * organization and member prices.
 */
export function VippsExpressButton({
  sku,
  label = sku ? "Kjøp med" : "Fortsett med",
  variant = "primary",
  className,
}: VippsExpressButtonProps) {
  const { universeTag } = useParams<{ universeTag: string }>();
  const { isLoaded, isSignedIn } = useAuth();
  const isEnabled = useVippsHurtigkasseFlag();
  const [isPending, startTransition] = useTransition();
  const [hasFailed, setHasFailed] = useState(false);
  const { trackSubmitted, trackSubmitFailed } = useTrackCheckoutSubmit("cart");

  if (!isEnabled || !isLoaded || isSignedIn) return null;

  const start = () => {
    setHasFailed(false);
    trackSubmitted(PAYMENT_PROVIDERS.VIPPS);
    startTransition(async () => {
      const result = await startVippsExpressCheckout({ universeTag, sku });
      // A successful start redirects to Vipps and never resolves here.
      if (result?.success === false) {
        trackSubmitFailed(result.status, PAYMENT_PROVIDERS.VIPPS);
        setHasFailed(true);
      }
    });
  };

  return (
    <div className={classNames("grid gap-2", className)}>
      <Button
        type="button"
        variant={variant}
        className={classNames("relative w-full gap-1.5 whitespace-nowrap", {
          // Keep the Vipps colours while the request runs instead of the
          // greyed-out disabled look, like AddToCartButton does.
          "disabled:bg-(--cvr-button-background) disabled:text-(--cvr-button-contrast)":
            isPending && variant === "primary",
          "disabled:border-(--cvr-button-background) disabled:bg-transparent disabled:text-(--cvr-button-background)":
            isPending && variant === "secondary",
        })}
        style={VIPPS_BUTTON_STYLE}
        onClick={start}
        disabled={isPending}
        aria-busy={isPending || undefined}
      >
        {/* Same pending look as AddToCartButton: the content keeps its space
            but is hidden behind a centered spinner. */}
        <span className={classNames({ "opacity-0": isPending })}>{label}</span>
        <VippsLogo
          role="img"
          aria-label="Vipps"
          height={15}
          className={classNames({ "opacity-0": isPending })}
        />
        {isPending && (
          <>
            <Spinner
              className="pointer-events-none absolute inset-0 m-auto"
              aria-hidden="true"
              width={21}
              height={21}
            />
            <span className="sr-only">Sender deg til Vipps</span>
          </>
        )}
      </Button>
      {hasFailed && (
        <p role="alert" className="text-sm text-status-error-500">
          Vi fikk ikke startet betalingen med Vipps. Prøv igjen, eller gå til
          kassen.
        </p>
      )}
    </div>
  );
}
